import { DestroyRef, inject, Injectable } from '@angular/core';
import { ChronoActivite, StatutActivite } from './chrono-activite.model';
import { BehaviorSubject } from 'rxjs';
import { LoggerFactory } from '../logger/logger-factory.service';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { GeoLocalisationProvider, LocationForActivite } from '../geolocalisation/geolocation.provider';
import {
  ActiviteEnCours,
  ActiviteEnCoursService,
  ActiviteEnCoursType,
} from '../../components/activite-en-cours/activite-en-cours.service';
import { Location } from '@capacitor-community/background-geolocation';
import { distance } from '../../utils/distance';
import { UserGeolocConsentService } from '../auth/user-geoloc-consent.service';
import { Capacitor } from '@capacitor/core';
import { getConfigOrElse } from '../config/config.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Preferences } from '@capacitor/preferences';

@Injectable({
  providedIn: 'root',
})
export class ChronoActiviteService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly userGeolocConsentService: UserGeolocConsentService = inject(UserGeolocConsentService);
  private readonly loggerFactory: LoggerFactory = inject(LoggerFactory);
  private readonly geoLocalisationProvider: GeoLocalisationProvider = inject(GeoLocalisationProvider);
  private readonly activiteEnCoursService: ActiviteEnCoursService = inject(ActiviteEnCoursService);
  private readonly logger: LoggerWrapper;

  chronoActiviteEnCours$: BehaviorSubject<ChronoActivite> = new BehaviorSubject(null);

  constructor() {
    this.logger = this.loggerFactory.getInstance(this, true);

    this.geoLocalisationProvider.lastPositionGps$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((locationForActivite: LocationForActivite) => {
        if (locationForActivite !== null) {
          this.recalculerStatutChrono(locationForActivite.idActivite, locationForActivite.location).then();
        }
      });
  }

  async demarrerChronoPourActivite(idActivite: string, typeActivite: ActiviteEnCoursType): Promise<ChronoActivite> {
    let chrono: ChronoActivite = await this.getChronoActivite(idActivite);
    if (chrono) {
      this.logger.info(`Chrono déjà démarré pour l'activite ${idActivite}`);
      return null;
    }
    chrono = new ChronoActivite(idActivite, await this.determinerStatutInitial(idActivite, typeActivite));
    await this.saveChronoActivite(idActivite, chrono);
    this.logger.info(`Démarrage d'un chrono pour l'activite ${idActivite}`, chrono);
    return chrono;
  }

  private async changerStatutChronoPourActivite(idActivite: string, statut: StatutActivite): Promise<ChronoActivite> {
    let chrono: ChronoActivite = await this.getChronoActivite(idActivite);
    if (!chrono) {
      this.logger.info(`Pas de chrono pour l'activite ${idActivite}`);
      return null;
    }
    chrono.stopperHorodatageEnCours();
    chrono.demarrerNouveauHorodatage(statut);
    await this.saveChronoActivite(idActivite, chrono);
    this.logger.info(`Changement d'état pour le chrono de l'activite ${idActivite}`, chrono);
    return chrono;
  }

  async arreterChronoPourActivite(idActivite: string): Promise<ChronoActivite> {
    let chrono: ChronoActivite = await this.getChronoActivite(idActivite);
    if (!chrono) {
      this.logger.info(`Pas de chrono pour l'activite ${idActivite}`);
      return null;
    }
    chrono.stopperHorodatageEnCours();
    await this.saveChronoActivite(idActivite, chrono);
    this.logger.info(`Arrêt du chrono de l'activite ${idActivite}`, chrono);
    return chrono;
  }

  async mettreChronoEnPausePourActivite(idActivite: string) {
    let chrono: ChronoActivite = await this.getChronoActivite(idActivite);
    if (!chrono) {
      this.logger.info(`Pas de chrono pour l'activite ${idActivite}`);
      return null;
    }
    chrono.stopperHorodatageEnCours();
    chrono.demarrerNouveauHorodatage(StatutActivite.pause);
    await this.saveChronoActivite(idActivite, chrono);
    this.logger.info(`Mise en pause du chrono de l'activite ${idActivite}`, chrono);
  }

  async reprendreChronoEnPausePourActivite(idActivite: string) {
    let chrono: ChronoActivite = await this.getChronoActivite(idActivite);
    if (!chrono) {
      this.logger.info(`Pas de chrono pour l'activite ${idActivite}`);
      return null;
    }

    chrono.stopperHorodatageEnCours();
    chrono.demarrerNouveauHorodatage(await this.determinerStatutInitial(idActivite, 'ot'));
    await this.saveChronoActivite(idActivite, chrono);
    this.logger.info(`Reprise du chrono de l'activite ${idActivite}`, chrono);
  }

  async supprimerChronoPourActivite(idActivite: string) {
    this.logger.info(`Suppression chrono de l'activite ${idActivite}`);
    await Preferences.remove({ key: this.getKeyChronoEnCours(idActivite) });
    this.chronoActiviteEnCours$.next(null);
  }

  async reloadChrono(idActivite: string) {
    console.log('reloadChrono pour activite' + idActivite);
    const chronoActivite: ChronoActivite = await this.getChronoActivite(idActivite);
    if (chronoActivite) {
      this.chronoActiviteEnCours$.next(chronoActivite);
    }
  }

  async getChronoActivite(idActivite: string): Promise<ChronoActivite> {
    const chronoStr = JSON.parse((await Preferences.get({ key: this.getKeyChronoEnCours(idActivite) })).value);
    if (chronoStr) {
      return Object.assign(new ChronoActivite(null), chronoStr);
    }
    return null;
  }

  private getKeyChronoEnCours(idActivite: string): string {
    return 'CHRONO_ACTIVITE_' + idActivite;
  }

  private async saveChronoActivite(idActivite: string, chrono: ChronoActivite) {
    await Preferences.set({
      key: this.getKeyChronoEnCours(idActivite),
      value: JSON.stringify(chrono),
    });
    this.chronoActiviteEnCours$.next(chrono);
  }

  private async recalculerStatutChrono(idActivite: string, location: Location) {
    let chrono: ChronoActivite = await this.getChronoActivite(idActivite);
    if (!chrono) {
      this.logger.info(`Pas de chrono pour l'activite ${idActivite}`);
      return;
    }
    const currentStatut: StatutActivite = chrono.horodatageEncours.statutActivite;
    if (currentStatut === StatutActivite.pause) {
      this.logger.info(`Chrono en pause pour l'activite ${idActivite}, pas de mise a jour`);
      return;
    }
    this.logger.info(`Recalcul du statut pour l'activite ${idActivite}`);
    let nouveauStatut = await this.calculerNouveauStatut(idActivite, location);
    if (currentStatut === StatutActivite.intervention && nouveauStatut === StatutActivite.deplacement) {
      this.logger.info(
        `Passage de interventaiuon à deplacement interdit pour l'activite ${idActivite}, pas de mise a jour`
      );
      return;
    }
    if (currentStatut !== nouveauStatut) {
      await this.changerStatutChronoPourActivite(idActivite, nouveauStatut);
    }
  }

  private async determinerStatutInitial(
    idActivite: string,
    typeActivite: ActiviteEnCoursType
  ): Promise<StatutActivite> {
    if (typeActivite === 'renfort' || typeActivite === 'activite-interne') {
      this.logger.debug(`DeterminerStatutInitial pour l'activite ${idActivite}: activite interne => intervention`);
      return StatutActivite.intervention;
    }

    if (!Capacitor.isNativePlatform() || !this.userGeolocConsentService.getCurrentConsentChrono()) {
      this.logger.debug(`DeterminerStatutInitial pour l'activite ${idActivite}: pas de consentement => deplacement`);
      return StatutActivite.deplacement;
    }
    const currentPosition = await this.geoLocalisationProvider.getCurrentPosition();
    this.logger.debug(
      `DeterminerStatutInitial pour l'activite ${idActivite}, currentPosition:`,
      currentPosition.coords
    );
    const currentLocation: Location = {
      latitude: currentPosition.coords.latitude,
      longitude: currentPosition.coords.longitude,
      accuracy: 0,
      altitude: 0,
      altitudeAccuracy: 0,
      bearing: 0,
      simulated: false,
      speed: 0,
      time: 0,
    };

    return this.calculerNouveauStatut(idActivite, currentLocation);
  }

  private async calculerNouveauStatut(idActivite: string, location: Location): Promise<StatutActivite> {
    let currentActivite: ActiviteEnCours = this.activiteEnCoursService.activiteEnCours$.getValue();
    if (currentActivite?.type === 'renfort' || currentActivite?.type === 'activite-interne') {
      this.logger.debug(`CalculerNouveauStatut pour l'activite ${idActivite}: activite interne => intervention`);
      return StatutActivite.intervention;
    }

    const geofenceRadiusMeter = Number(getConfigOrElse('GEOFENCE_RADIUS_METRES', '50'));

    if (
      !Capacitor.isNativePlatform() ||
      !this.userGeolocConsentService.getCurrentConsentChrono() ||
      !currentActivite.siteLocations ||
      currentActivite.siteLocations.length === 0
    ) {
      this.logger.debug(
        `CalculerNouveauStatut pour l'activite ${idActivite}: pas de consentement ou pas de siteLocation => deplacement`
      );
      return StatutActivite.deplacement;
    }
    const nearbySiteAddress = this.findNearbySiteAddress(
      currentActivite.siteLocations,
      idActivite,
      location,
      geofenceRadiusMeter
    );

    if (nearbySiteAddress) {
      this.logger.debug(
        `CalculerNouveauStatut pour l'activite ${idActivite}: moins de ${geofenceRadiusMeter}m => intervention`
      );
      return StatutActivite.intervention;
    }

    this.logger.debug(
      `CalculerNouveauStatut pour l'activite ${idActivite}: plus de ${geofenceRadiusMeter}m => deplacement`
    );
    return StatutActivite.deplacement;
  }

  private findNearbySiteAddress(
    siteAddressList: any[],
    idActivite: string,
    location: Location,
    geofenceRadiusMeter: number
  ) {
    return siteAddressList.find(
      (mappedLocation) => this.calculerDistanceAvecSite(idActivite, location, mappedLocation) <= geofenceRadiusMeter
    );
  }

  private calculerDistanceAvecSite(idActivite: string, userPosition: Location, sitePosition: Location): number {
    const userCoord = {
      gps_x: String(userPosition.longitude),
      gps_y: String(userPosition.latitude),
    };
    this.logger.debug(`Position Utilisateur pour l'activite ${idActivite}`, userCoord);
    const siteCoord = {
      gps_x: String(sitePosition.longitude),
      gps_y: String(sitePosition.latitude),
    };
    this.logger.debug(`Position Site pour l'activite ${idActivite}`, siteCoord);

    const number = distance(userCoord, siteCoord);
    this.logger.debug(`Distance calculée pour l'activite ${idActivite}: ${number} m`);
    return number;
  }
}
